import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { X, ArrowLeft } from '../../icons';

let openCount = 0;
const lockBody = () => {
  openCount += 1;
  if (openCount === 1 && typeof document !== 'undefined') {
    document.body.dataset.prevOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }
};
const unlockBody = () => {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0 && typeof document !== 'undefined') {
    document.body.style.overflow = document.body.dataset.prevOverflow || '';
    delete document.body.dataset.prevOverflow;
  }
};

const SIZES = {
  sm: 'lg:max-w-md',
  md: 'lg:max-w-2xl',
  lg: 'lg:max-w-4xl',
  xl: 'lg:max-w-6xl',
  full: 'lg:w-[92vw] lg:max-w-[1400px] lg:h-[92vh]',
};

/**
 * Sheet — one overlay primitive, three modes:
 *  - `sheet`  : bottom sheet on mobile, centered dialog on desktop (default)
 *  - `dialog` : centered dialog on all breakpoints
 *  - `page`   : full-screen page on mobile, centered large dialog on desktop
 *
 * Closes on Escape, backdrop click or the header close button. Locks body scroll while open.
 */
const Sheet = ({
  isOpen,
  onClose,
  mode = 'sheet',
  size = 'md',
  title,
  subtitle,
  headerLeft,
  headerRight,
  footer,
  hideHeader = false,
  className,
  bodyClassName,
  children,
  'aria-label': ariaLabel,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockBody();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    // Focus the panel so Escape works immediately and screen readers announce it.
    const t = setTimeout(() => panelRef.current && panelRef.current.focus(), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      unlockBody();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isPage = mode === 'page';
  const isDialog = mode === 'dialog';

  // NOTE: `relative` must not be in the shared base — Tailwind emits it after `.fixed`,
  // which would override the full-screen `fixed inset-0` page panel on mobile.
  const panelClasses = cn(
    'flex flex-col bg-card text-primary shadow-pop outline-none border border-line',
    // Mobile presentation
    isPage
      ? 'fixed inset-0 w-full max-w-full rounded-none border-0 animate-slide-in-right'
      : isDialog
        ? 'relative w-[calc(100vw-2rem)] max-h-[88vh] rounded-sheet animate-pop-in'
        : 'relative w-full max-h-[92vh] rounded-t-sheet border-b-0 animate-slide-up',
    // Desktop presentation
    isPage
      ? 'lg:relative lg:inset-auto lg:rounded-sheet lg:border lg:animate-pop-in lg:w-[92vw] lg:max-w-[1400px] lg:h-[92vh]'
      : cn('lg:w-full lg:rounded-sheet lg:border lg:max-h-[88vh] lg:animate-pop-in', SIZES[size] || SIZES.md),
    className
  );

  const containerClasses = cn(
    'fixed inset-0 z-[100] flex animate-fade-in',
    isPage ? 'lg:items-center lg:justify-center lg:p-4' : isDialog ? 'items-center justify-center p-4' : 'items-end justify-center lg:items-center lg:p-4'
  );

  const content = (
    <div className={containerClasses} role="presentation">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#060A14]/70"
        data-testid="sheet-backdrop"
        onClick={() => onClose && onClose()}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : 'Dialog')}
        tabIndex={-1}
        className={panelClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle for bottom sheets */}
        {!isPage && !isDialog && (
          <div className="lg:hidden flex justify-center pt-2.5 pb-1">
            <span className="w-10 h-1.5 rounded-full bg-line-strong" />
          </div>
        )}

        {!hideHeader && (
          <header
            className={cn(
              'flex items-start gap-3 px-4 lg:px-6 pb-3 border-b border-line flex-shrink-0',
              isPage ? 'pt-safe' : '',
              isPage ? 'pt-4' : 'pt-3 lg:pt-5'
            )}
          >
            {isPage && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Back"
                className="lg:hidden -ml-1 w-11 h-11 flex items-center justify-center rounded-xl text-secondary hover:text-primary hover:bg-white/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            {headerLeft}
            <div className="flex-1 min-w-0 pt-2">
              {title && (
                <h2 className="text-lg lg:text-xl font-semibold leading-tight text-primary truncate">
                  {title}
                </h2>
              )}
              {subtitle && <p className="text-sm text-secondary mt-0.5 truncate">{subtitle}</p>}
            </div>
            {headerRight}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={cn(
                'w-11 h-11 flex items-center justify-center rounded-xl text-secondary hover:text-primary hover:bg-white/5 flex-shrink-0',
                isPage && 'hidden lg:flex'
              )}
            >
              <X className="w-5 h-5" />
            </button>
          </header>
        )}

        <div className={cn('flex-1 overflow-y-auto px-4 lg:px-6 py-4', !isPage && 'pb-safe', bodyClassName)}>
          {children}
        </div>

        {footer && (
          <footer className="flex-shrink-0 px-4 lg:px-6 py-3 border-t border-line bg-card pb-safe">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default Sheet;
